import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import PageTitle from '../../FormElements/PageTitle';
import { useAuth } from '../../../contexts/AuthContext';
import SegmentedControl from '../../ChallengeWire/SegmentedControl';
import ChallengeWireChallengeSections from '../../ChallengeWire/ChallengeWireChallengeSections';
import ChallengeResolveActions from '../../ChallengeWire/ChallengeResolveActions';
import ChallengeCard, { challengeCardBtn } from '../../ChallengeWire/ChallengeCard';
import HonorCodeModal from '../../ChallengeWire/HonorCodeModal';
import SendChallengeSheet from '../../ChallengeWire/SendChallengeSheet';
import PostCommunityChallengeSheet from '../../ChallengeWire/PostCommunityChallengeSheet';
import CommunityChallengeListCard from '../../ChallengeWire/CommunityChallengeListCard';
import { fetchFriendChallenges, fetchFriendships, fetchHouseholdChallenges } from '../../../utils/challengeWireApi';
import { fetchCommunityChallenges } from '../../../utils/communityChallengeApi';
import { resolveChallengeWireViewer } from '../../../utils/challengeWireViewer';
import { fetchUserFitnessProfiles } from '../../../utils/fitnessProfilesApi';
import useChallengeActions, { challengeHasActions } from '../../../hooks/useChallengeActions';
import useChallengeDayChecks from '../../../hooks/useChallengeDayChecks';
import { mergeChallengeDayTracking } from '../../../utils/challengeDayTracking';
import { celebrateChallengeComplete } from '../../../utils/celebrationConfetti';
import { honorCodeFirstName } from '../../../constants/honorCode';
import { groupChallengesForAllView, sortChallengesByRecency } from '../../../utils/challengePerspective';
import { pageMutedTextClass } from '../../ChallengeWire/challengeWireFormStyles';
import useSubscriptionGate from '../../../hooks/useSubscriptionGate';

export default function ChallengeWirePage() {
	const { user, csrfToken, endpoints, activeFitnessProfile } = useAuth();
	const { guardSubscription } = useSubscriptionGate();
	const location = useLocation();
	const navigate = useNavigate();
	const [track, setTrack] = useState('all');
	const [friendTab, setFriendTab] = useState('incoming');
	const [householdTab, setHouseholdTab] = useState('active');
	const [friendChallenges, setFriendChallenges] = useState([]);
	const [householdChallenges, setHouseholdChallenges] = useState([]);
	const [friends, setFriends] = useState([]);
	const [profiles, setProfiles] = useState([]);
	const [loading, setLoading] = useState(true);
	const [sendOpen, setSendOpen] = useState(false);
	const [communityPostOpen, setCommunityPostOpen] = useState(false);
	const [communityChallenges, setCommunityChallenges] = useState([]);
	const [honorChallenge, setHonorChallenge] = useState(null);
	const [acceptLoading, setAcceptLoading] = useState(false);
	const [acceptError, setAcceptError] = useState('');
	const [error, setError] = useState('');

	const initialRecipientKey = location.state?.friendId
		? `friend:${location.state.friendId}`
		: location.state?.fitnessProfileId
			? `profile:${location.state.fitnessProfileId}`
			: '';
	const openSendInitially = location.state?.openSend;

	const viewer = useMemo(
		() => resolveChallengeWireViewer({ activeFitnessProfile, profiles }),
		[activeFitnessProfile, profiles]
	);

	const trackOptions = useMemo(
		() =>
			viewer.isHouseholdSession
				? [
						{ value: 'all', label: 'All' },
						{ value: 'household', label: 'Household' }
					]
				: [
						{ value: 'all', label: 'All' },
						{ value: 'community', label: 'Community' },
						{ value: 'friends', label: 'Friends' },
						{ value: 'household', label: 'Household' }
					],
		[viewer.isHouseholdSession]
	);

	useEffect(() => {
		if (openSendInitially) {
			guardSubscription(() => setSendOpen(true));
		}
	}, [openSendInitially, guardSubscription]);

	useEffect(() => {
		if (viewer.isHouseholdSession && track === 'friends') {
			setTrack('all');
		}
	}, [viewer.isHouseholdSession, track]);

	useEffect(() => {
		if (!user?.id || !endpoints?.get_user_fitness_profiles_endpoint) return;
		fetchUserFitnessProfiles({
			userId: user.id,
			endpoint: endpoints.get_user_fitness_profiles_endpoint,
			csrfToken
		})
			.then(setProfiles)
			.catch(() => setProfiles([]));
	}, [user?.id, endpoints, csrfToken]);

	const loadFriends = useCallback(async () => {
		if (!user?.id) return;
		const data = await fetchFriendships({
			userId: user.id,
			endpoint: endpoints.get_user_friendships_endpoint,
			csrfToken
		});
		setFriends(data.friends || []);
	}, [user?.id, endpoints, csrfToken]);

	const householdQuery = useMemo(
		() => ({
			userId: user?.id,
			endpoint: endpoints.get_user_fitness_profile_challenges_endpoint,
			csrfToken,
			...(viewer.fitnessProfileId ? { fitnessProfileId: viewer.fitnessProfileId } : {})
		}),
		[user?.id, endpoints, csrfToken, viewer.fitnessProfileId]
	);

	const loadChallenges = useCallback(async () => {
		if (!user?.id) return;
		setLoading(true);
		setError('');
		try {
			await loadFriends();
			const communityPromise = !viewer.isHouseholdSession
				? fetchCommunityChallenges({
						userId: user.id,
						endpoint: endpoints.get_user_community_challenges_endpoint,
						csrfToken,
					})
				: Promise.resolve([]);

			if (track === 'all') {
				const [friendsData, householdData, communityData] = await Promise.all([
					fetchFriendChallenges({
						userId: user.id,
						endpoint: endpoints.get_user_fitness_challenges_endpoint,
						csrfToken
					}),
					fetchHouseholdChallenges(householdQuery),
					communityPromise
				]);
				setFriendChallenges(friendsData);
				setHouseholdChallenges(householdData);
				setCommunityChallenges(communityData);
			} else if (track === 'community') {
				const communityData = await communityPromise;
				setCommunityChallenges(communityData);
				setFriendChallenges([]);
				setHouseholdChallenges([]);
			} else if (track === 'friends') {
				const data = await fetchFriendChallenges({
					userId: user.id,
					endpoint: endpoints.get_user_fitness_challenges_endpoint,
					csrfToken,
					role: friendTab
				});
				setFriendChallenges(data);
				setHouseholdChallenges([]);
				setCommunityChallenges([]);
			} else {
				const data = await fetchHouseholdChallenges({
					...householdQuery,
					status: householdTab
				});
				setHouseholdChallenges(data);
				setFriendChallenges([]);
				setCommunityChallenges([]);
			}
		} catch (e) {
			setError(e.message);
		} finally {
			setLoading(false);
		}
	}, [user?.id, endpoints, csrfToken, track, friendTab, householdTab, loadFriends, householdQuery]);

	useEffect(() => {
		loadChallenges();
	}, [loadChallenges]);

	const { runAction } = useChallengeActions({
		user,
		csrfToken,
		endpoints,
		onAfterAction: loadChallenges
	});

	const { toggleDayCheck, isDayCheckPending } = useChallengeDayChecks({
		user,
		csrfToken,
		endpoints
	});

	const handleDayTrackingUpdate = useCallback(
		(challenge, dayTracking) => {
			if (challenge.kind === 'friend') {
				setFriendChallenges((prev) => mergeChallengeDayTracking(prev, challenge, dayTracking));
			} else {
				setHouseholdChallenges((prev) => mergeChallengeDayTracking(prev, challenge, dayTracking));
			}
		},
		[]
	);

	const handleDayCheck = useCallback(
		(challenge, isoDate, checked) =>
			toggleDayCheck(challenge, isoDate, checked, { onUpdate: handleDayTrackingUpdate }).catch((e) =>
				setError(e.message)
			),
		[toggleDayCheck, handleDayTrackingUpdate]
	);

	function handleAcceptConfirm() {
		if (!honorChallenge || acceptLoading) return;
		setAcceptError('');
		setAcceptLoading(true);
		runAction(honorChallenge, 'accept')
			.then(() => setHonorChallenge(null))
			.catch((e) => {
				if (!e.sessionInvalid) setAcceptError(e.message);
			})
			.finally(() => setAcceptLoading(false));
	}

	function closeHonorModal() {
		if (acceptLoading) return;
		setHonorChallenge(null);
		setAcceptError('');
	}

	function honorModalProps(challenge) {
		if (!challenge) return { title: 'Accept challenge' };
		if (challenge.kind === 'household' || challenge.kind === 'linked') {
			return {
				acceptingAsName: honorCodeFirstName(challenge.challenged_profile.name),
				challengeTitle: challenge.title
			};
		}
		return { title: `Accept: ${challenge.title}` };
	}

	const friendTabOptions = [
		{ value: 'incoming', label: 'For you' },
		{ value: 'sent', label: 'You sent' },
		{ value: 'active', label: 'Active' },
		{ value: 'history', label: 'History' }
	];

	const householdTabOptions = [
		{ value: 'active', label: 'Active' },
		{ value: 'history', label: 'History' }
	];

	const list =
		track === 'all'
			? sortChallengesByRecency([...friendChallenges, ...householdChallenges])
			: track === 'friends'
				? friendChallenges
				: track === 'community'
					? []
					: householdChallenges;

	const activeCommunity =
		track === 'community'
			? communityChallenges
			: track === 'all'
				? communityChallenges.filter((c) => c.status === 'active' || c.status === 'open')
				: [];

	function handleCompleteChallenge(challenge) {
		return runAction(challenge, 'complete')
			.then(() => celebrateChallengeComplete())
			.catch((e) => setError(e.message));
	}

	function renderChallengeGrid(challenges) {
		return (
			<ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
				{challenges.map((c) => (
					<li key={`${c.kind}-${c.id}`} className="h-full min-h-0">
						<ChallengeCard
							challenge={c}
							onDayCheck={handleDayCheck}
							isDayCheckPending={isDayCheckPending}
						>
							{renderChallengeActions(c)}
						</ChallengeCard>
					</li>
				))}
			</ul>
		);
	}

	function renderChallengeActions(c) {
		if (!challengeHasActions(c)) return null;

		return (
			<>
				{c.can_accept && (
					<>
						<button
							type="button"
							onClick={() => setHonorChallenge(c)}
							className={challengeCardBtn.primary}
						>
							Accept
						</button>
						<button
							type="button"
							onClick={() => runAction(c, 'decline').catch((e) => setError(e.message))}
							className={challengeCardBtn.secondary}
						>
							Decline
						</button>
					</>
				)}
				{c.can_fail && (
					<ChallengeResolveActions
						challenge={c}
						onComplete={() => handleCompleteChallenge(c)}
						onFail={() => runAction(c, 'fail').catch((e) => setError(e.message))}
					/>
				)}
				{c.can_cancel && (
					<span className={c.can_accept || c.can_fail ? 'ml-auto' : ''}>
						<button
							type="button"
							onClick={() => runAction(c, 'cancel').catch((e) => setError(e.message))}
							className={challengeCardBtn.ghost}
						>
							Cancel
						</button>
					</span>
				)}
			</>
		);
	}

	return (
		<div className="container p-4 pt-4 lg:py-12 md:px-20 max-w-6xl mx-auto">
			<div className="flex flex-wrap items-center justify-between gap-3">
				<PageTitle title="Challenge Wire" />
				<Link
					to="/fitness-hub"
					className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline"
				>
					← Fitness Hub
				</Link>
			</div>

			<div className="mt-6 flex flex-wrap items-center justify-between gap-3">
				<SegmentedControl options={trackOptions} value={track} onChange={setTrack} />
				<div className="flex flex-wrap gap-2">
					{!viewer.isHouseholdSession && (
						<button
							type="button"
							onClick={() => guardSubscription(() => setCommunityPostOpen(true))}
							className="rounded-xl border border-blue-600 px-4 py-2.5 text-sm font-semibold text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-dark-bg"
						>
							+ Community
						</button>
					)}
					<button
						type="button"
						onClick={() => guardSubscription(() => setSendOpen(true))}
						className="rounded-xl bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-blue-700"
					>
						+ New challenge
					</button>
				</div>
			</div>

			{track !== 'all' && (
				<div className="mt-4">
					{track === 'friends' ? (
						<SegmentedControl options={friendTabOptions} value={friendTab} onChange={setFriendTab} />
					) : (
						<SegmentedControl options={householdTabOptions} value={householdTab} onChange={setHouseholdTab} />
					)}
				</div>
			)}

			{error && <p className="mt-4 text-sm text-red-600 dark:text-red-400">{error}</p>}

			{loading ? (
				<p className={`mt-8 ${pageMutedTextClass}`}>Loading…</p>
			) : track === 'community' ? (
				<div className="mt-6">
					{communityChallenges.length === 0 ? (
						<p className={pageMutedTextClass}>
							No community challenges yet. Post one to your friend network.
						</p>
					) : (
						<ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
							{communityChallenges.map((c) => (
								<li key={c.id}>
									<CommunityChallengeListCard challenge={c} />
								</li>
							))}
						</ul>
					)}
				</div>
			) : list.length === 0 && activeCommunity.length === 0 ? (
				<p className={`mt-8 ${pageMutedTextClass}`}>
					No challenges here yet. Tap{' '}
					<strong className="text-gray-800 dark:text-gray-200">New challenge</strong> to start.
				</p>
			) : track === 'all' ? (
				<div className="mt-6 space-y-8">
					{activeCommunity.length > 0 && (
						<section>
							<h2 className="text-sm font-semibold uppercase tracking-wide text-gray-700 dark:text-gray-200 mb-4">
								Community
							</h2>
							<ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
								{activeCommunity.map((c) => (
									<li key={c.id}>
										<CommunityChallengeListCard challenge={c} />
									</li>
								))}
							</ul>
						</section>
					)}
					<ChallengeWireChallengeSections
						sections={groupChallengesForAllView(list)}
						renderGrid={renderChallengeGrid}
					/>
				</div>
			) : (
				<div className="mt-6">{renderChallengeGrid(list)}</div>
			)}

			<SendChallengeSheet
				isOpen={sendOpen}
				onClose={() => setSendOpen(false)}
				user={user}
				csrfToken={csrfToken}
				endpoints={endpoints}
				friends={friends}
				initialRecipientKey={initialRecipientKey}
				onSent={loadChallenges}
			/>

			<PostCommunityChallengeSheet
				isOpen={communityPostOpen}
				onClose={() => setCommunityPostOpen(false)}
				user={user}
				csrfToken={csrfToken}
				endpoints={endpoints}
				profiles={profiles}
				onPosted={(created) => {
					setCommunityPostOpen(false);
					if (created?.id) {
						navigate(`/community-challenges/${created.id}`);
						return;
					}
					loadChallenges();
				}}
			/>

			<HonorCodeModal
				isOpen={!!honorChallenge}
				onClose={closeHonorModal}
				onConfirm={handleAcceptConfirm}
				loading={acceptLoading}
				error={acceptError}
				{...honorModalProps(honorChallenge)}
			/>
		</div>
	);
}
